import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Check, KeyRound, UserCheck, Users } from 'lucide-react'
import { api, type IntegratorRow } from '../api/client'
import { useActiveIntegrator } from '../auth/active-integrator'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

export function IntegratorDetailPage() {
  const { integratorId = '' } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const active = useActiveIntegrator()
  const [rotateMsg, setRotateMsg] = useState<string | null>(null)

  // List endpoint used as a stand-in for a single-integrator GET so we
  // avoid growing the client without a backend change. For a 100-row
  // local dev the perf cost is a non-issue; swap to GET /v1/admin/integrators/:id
  // once the backend ships one.
  const { data, isLoading, error } = useQuery({
    queryKey: ['integrators'],
    queryFn: () => api.integrators.list(100, 0),
  })
  const integrator: IntegratorRow | undefined = useMemo(
    () => data?.find((i) => i.id === integratorId),
    [data, integratorId],
  )

  const rotate = useMutation({
    mutationFn: () => api.integrators.rotateKey(integratorId),
    onSuccess: (res) => {
      setRotateMsg(`new signing key: ${res.keyId} (${res.algorithm}, ${res.status})`)
      qc.invalidateQueries({ queryKey: ['integrators'] })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-56 w-full" />
      </div>
    )
  }
  if (error) {
    return <div className="text-destructive">Failed to load: {(error as Error).message}</div>
  }
  if (!integrator) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground">Unknown integrator.</div>
        <Button variant="outline" onClick={() => nav('/integrators')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to list
        </Button>
      </div>
    )
  }

  const isActive = active.integratorId === integrator.id

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav('/integrators')}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Integrators
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{integrator.name}</h1>
          <div className="font-mono text-xs text-muted-foreground">{integrator.slug ?? '—'}</div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={isActive ? 'secondary' : 'default'}
            disabled={isActive}
            onClick={() => active.setActive(integrator.id, integrator.name)}
          >
            {isActive
              ? <><Check className="mr-1 h-4 w-4" /> Already active</>
              : <><UserCheck className="mr-1 h-4 w-4" /> Operate as this integrator</>}
          </Button>
          <Button asChild variant="outline">
            <Link to={`/integrators/${integrator.id}/merchants`}>
              <Users className="mr-1 h-4 w-4" />
              Merchants
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Core settings — edit from the list view.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <KV k="Status">
              <Badge variant={integrator.status === 'active' ? 'default' : 'secondary'}>
                {integrator.status}
              </Badge>
            </KV>
            <KV k="Signing">
              <span className="text-muted-foreground">
                {integrator.signingEnabled === false ? 'disabled' : 'enabled'}
              </span>
            </KV>
            <KV k="Callback URL">
              <span className="break-all font-mono text-xs">
                {integrator.callbackUrl ?? <span className="italic text-muted-foreground">none</span>}
              </span>
            </KV>
            <KV k="ID">
              <span className="font-mono text-xs">{integrator.id}</span>
            </KV>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Signing key rotation</CardTitle>
            <CardDescription>
              Mints a new Ed25519 key, marks the previous as rotating, and keeps
              both valid for the configured overlap window.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="destructive"
              disabled={rotate.isPending}
              onClick={() => {
                if (!window.confirm(`Rotate signing key for ${integrator.name}?`)) return
                setRotateMsg(null)
                rotate.mutate()
              }}
            >
              <KeyRound className="mr-2 h-4 w-4" />
              {rotate.isPending ? 'Rotating…' : 'Rotate signing key'}
            </Button>
            {rotateMsg && (
              <div className="rounded border border-primary/30 bg-primary/5 p-2 text-xs">{rotateMsg}</div>
            )}
            {rotate.error && (
              <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                {(rotate.error as Error).message}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function KV({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{k}</span>
      <span>{children}</span>
    </div>
  )
}
